import { Field, useFormikContext } from 'formik';
import { FC, useEffect } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { validateItemLength } from '../../utilities/Validation';
import Label from '../label/label';
import Togglebox from '../togglebox/togglebox';
import { LICENSE_APERIO_OSS } from "../../constants/license-constants";
import { useGlobalState } from "../../Providers";

export const SiteDetails: FC = () => {
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();
    const hexValues = /\d|[a-fA-F]/

    const restrictLength = (e, length) => {
        if (e.key !== 'Backspace'
            && e.key !== 'Tab'
            && (!e.key.match(hexValues)
                || e.target.value.length === length
                || e.target.value.length > length)) {
            e.preventDefault();
            
        }
    }

    /**
     * Snap the value to the nearest valid increment of 24.
     * This ensures that the validity duration for OSS is always in increments of 24 hours, as required by the system.
     * @param value The input value to be snapped.
     * @returns The snapped value, rounded to the nearest multiple of 24.
     */
    const snapToValidIncrement = (value) => {
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 24) {
            return 24;
        }
        // Round to nearest multiple of 24
        return Math.round(numValue / 24) * 24;
    }

    useEffect(() => {
        if (!values.tagmask) setFieldValue('tagmask', 'FFFFFFFFFFFFF')
    }, [])

    return <>
        <div className="flex flex-row">
            <div className="flex flex-col flex-1 pr-3" id='tagMaskLabel'>
                <Label htmlFor='tagmask'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.tagmask)}:</Label>
                <Field name="tagmask" id='tagMaskInputfield'
                    type="text"
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full"
                    onKeyDown={e => restrictLength(e, 13)}
                    onPaste={(e) => { e.preventDefault(); }}
                    validate={val => validateItemLength(val, 13, translate)}>
                </Field>
                {errors.tagmask ? <span className='text-red-600 text-xs'>{errors.tagmask}</span> : null}
            </div>

            <div className="flex flex-col flex-1 pl-3">
                <Label htmlFor='tagmaskRemovezeros'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.tagmaskRemovezeros)}:</Label>
                <Togglebox name="tagmaskRemovezeros" label={''} />
            </div>
        </div>
        {isLicensedForAperioOss && (
            <div className="flex flex-row pt-2">
                <div className="flex flex-col flex-1 pr-3" id='ossIdLabel'>
                    <Label htmlFor='ossId'
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.ossSiteId)}:</Label>
                    <Field name="ossId" id='ossIdInputfield'
                        type="number"
                        min={0} // Minimum is actually 1, but we allow 0 which mean "no OSS site ID" for this site.
                        max={65535}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full">
                    </Field>
                    {errors.ossId ? <span className='text-red-600 text-xs'>{errors.ossId}</span> : null}
                </div>

                <div className="flex flex-col flex-1 pl-3">
                    <Label htmlFor='ossValidityDuration'
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.ossValidityDuration)}:</Label>
                    <Field name="ossValidityDuration" id='ossValidityDurationInputfield'
                        type="number"
                        min={24}
                        step={24}
                        defaultValue={336} // Default to 2 weeks (336 hours) if not set. Recommended by FW team.
                        onBlur={(e) => {
                            const snappedValue = snapToValidIncrement(e.target.value);
                            setFieldValue('ossValidityDuration', snappedValue);
                        }}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full">
                    </Field>
                    {errors.ossValidityDuration ? <span className='text-red-600 text-xs'>{errors.ossValidityDuration}</span> : null}
                </div>
            </div>)}
    </>;
};
